import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface Sponsor {
  id: string;
  name: string;
  type: string;
  logo_url?: string;
  description?: string;
  website_url?: string;
}

const SponsorsSection = () => {
  const { t } = useTranslation();
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);

  useEffect(() => {
    const fetchSponsors = async () => {
      const { data } = await supabase
        .from('sponsors')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (data) setSponsors(data);
    };

    fetchSponsors();
  }, []);

  if (sponsors.length === 0) return null;

  return (
    <section className="py-20 bg-muted/30">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t("sponsors.title")}</h2>
          <p className="text-muted-foreground text-lg">{t("sponsors.subtitle")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sponsors.map((sponsor) => {
            const CardWrapper = sponsor.website_url ? 'a' : 'div';
            const cardProps = sponsor.website_url
              ? { href: sponsor.website_url, target: '_blank', rel: 'noopener noreferrer' }
              : {};

            return (
              <CardWrapper key={sponsor.id} {...cardProps} className="block">
                <Card className="hover:shadow-lg transition-shadow h-full cursor-pointer">
                  <CardHeader>
                    <div className="flex items-center gap-4">
                      {sponsor.logo_url && (
                        <img 
                          src={sponsor.logo_url} 
                          alt={sponsor.name}
                          className="w-16 h-16 object-contain"
                          loading="lazy"
                          width="64"
                          height="64"
                        />
                      )}
                      <div>
                        <CardTitle className="text-lg">{sponsor.name}</CardTitle>
                        <span className="text-xs text-muted-foreground capitalize">{sponsor.type}</span>
                      </div>
                    </div>
                  </CardHeader>
                  {sponsor.description && (
                    <CardContent>
                      <p className="text-sm text-muted-foreground">{sponsor.description}</p>
                    </CardContent>
                  )}
                </Card>
              </CardWrapper>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default SponsorsSection;